import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { toast } from 'react-toastify';
import { Toaster } from '../platform-components/Toaster';
import { canForceDeleteQuestion, deleteQuestions } from './question-delete.service';

/**
 * The whole delete-a-question interaction in one controlled component: confirm, the server's
 * refusal, and the "Delete anyway" override.
 *
 * Every surface that can delete a question renders this - the Question Repo table, the question
 * popup, the Dashboard's bulk delete - so the confirmation wording, the override gate and the
 * error handling stay identical across all of them.
 *
 * Flow:
 *   1. Confirm - "Delete question N?"
 *   2. The API answers 409 when the question is still mapped into a module or a test, with one
 *      bullet per mapping. That message is shown as-is.
 *   3. If the user has "Force Delete Question", the refusal offers "Delete anyway", which resends
 *      with force=true. The server then removes those mappings itself - recomputing each
 *      container's question_count and evicting its cache - before deleting the question, so the
 *      override never leaves the orphan map rows the gate exists to prevent.
 *   4. A force attempt that still fails (a question students have already answered, or a missing
 *      permission) shows the reason with no second override, because there is not one.
 *
 * @param {boolean}   open         render the confirm step
 * @param {number[]}  questionIds  ids to delete; the server refuses the whole batch or none of it
 * @param {Function}  onClose      called whenever the flow ends, deleted or not
 * @param {Function}  onDeleted    called after a successful delete, before onClose
 */
export default function DeleteQuestionDialog({ open, questionIds, onClose, onDeleted }) {
  const [blockedMessage, setBlockedMessage] = useState('');
  // Whether the refusal on screen came from an attempt that ALREADY used the override. The server
  // has no second override to offer, so the button must not be shown again - otherwise the user
  // clicks it repeatedly against a refusal (an attempted question) that will never change.
  const [forceAttempted, setForceAttempted] = useState(false);
  const [busy, setBusy] = useState(false);

  const ids = questionIds || [];
  const label = ids.length === 1 ? `Question Id: ${ids[0]}` : `${ids.length} questions`;
  // Keyed on the ids themselves, not the array identity. Most hosts build the prop inline
  // (`[Number(deletedQnId)]`), so a fresh array arrives on every parent render - depending on the
  // reference would clear the refusal on screen the moment anything else re-rendered the page.
  const idsKey = ids.join(',');

  // A new confirm must not inherit the previous question's refusal.
  useEffect(() => {
    if (open) {
      setBlockedMessage('');
      setForceAttempted(false);
    }
  }, [open, idsKey]);

  const close = () => {
    setBlockedMessage('');
    setForceAttempted(false);
    setBusy(false);
    if (onClose) onClose();
  };

  const runDelete = async (force) => {
    if (!ids.length) {
      // Nothing selected - the hosts disable their buttons for this, so it means the selection was
      // cleared while the confirm was open. Closing is the honest outcome; sending an empty list
      // would just come back as a server error.
      close();
      return;
    }
    setBusy(true);
    const result = await deleteQuestions(ids, { force });
    setBusy(false);
    if (result.ok) {
      Toaster(
        toast.TYPE.SUCCESS,
        ids.length === 1 ? 'Question deleted Successfully' : 'Questions deleted Successfully'
      );
      // close() BEFORE onDeleted(): hosts that render this inside a `{selectedQuestion && ...}`
      // branch unmount it as part of their refresh, and a setState after that is a no-op with a
      // console warning attached.
      close();
      if (onDeleted) onDeleted();
      return;
    }
    setForceAttempted(force);
    setBlockedMessage(result.message);
  };

  // Only offered on a refusal that has not already been overridden, and only to a role that has
  // been granted it - the server enforces the same rule, so a stale permission just yields a 403
  // shown in this same dialog.
  const canOverride = !!blockedMessage && !forceAttempted && canForceDeleteQuestion();

  return (
    <>
      <Dialog
        open={!!open && !blockedMessage}
        onClose={close}
        aria-labelledby="delete-question-title"
        aria-describedby="delete-question-description"
      >
        <DialogContent>
          <DialogContentText id="delete-question-description">
            Permanently delete <strong>{label}</strong> from the question bank?
          </DialogContentText>
          {/* Spelled out because this dialog also opens from inside a QB module and from inside a
              test, where the row's bin icon means "remove from THIS one". These are not the same
              action and the two sit two clicks apart. */}
          <DialogContentText style={{ marginTop: '12px' }}>
            This deletes the question itself, everywhere — not just its place in the module or test
            you are looking at. If it is still used anywhere, you will be shown the full list before
            anything is removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={close} disabled={busy}>
            Close
          </Button>
          <Button variant="contained" onClick={() => runDelete(false)} color="error" disabled={busy} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* The server's refusal. The message carries newline-separated bullets, so preserve them. */}
      <Dialog
        open={!!blockedMessage}
        onClose={close}
        aria-labelledby="delete-question-blocked-title"
        aria-describedby="delete-question-blocked-description"
      >
        <DialogTitle id="delete-question-blocked-title">Delete refused</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-question-blocked-description" style={{ whiteSpace: 'pre-line' }}>
            {blockedMessage}
          </DialogContentText>
          {canOverride && (
            <DialogContentText style={{ marginTop: '16px', color: '#b71c1c' }}>
              <strong>Delete anyway</strong> removes the question from everything listed above and
              then deletes it. The affected modules and tests have their question counts corrected
              automatically, but any student who has not started yet will see one question fewer.
              This cannot be undone.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={close} disabled={busy} autoFocus>
            Close
          </Button>
          {canOverride && (
            <Button variant="contained" color="error" onClick={() => runDelete(true)} disabled={busy}>
              Delete anyway
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
